import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const EnterpriseMsmeTable = () => {
  const pid = useSelector((state) => state.user.projectId);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  // Date Filters - State for input fields
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");

  // Date Filters - State for actual filter values used in API
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Apply Filter Handler
  const handleApplyFilter = () => {
    setStartDate(startDateInput);
    setEndDate(endDateInput);
  };

  // Clear Filter Handler
  const handleClearFilter = () => {
    setStartDateInput("");
    setEndDateInput("");
    setStartDate("");
    setEndDate("");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let url = `https://api-project360-insights.haqdarshak.com/kpi/msme/enterprise-msme/?pid=${pid}`;

        // If date filters are applied, use the new API with date parameters
        if (startDate || endDate) {
          url = `https://api-project360-insights.haqdarshak.com/kpi/msme/enterprise-report/?pid=${pid}`;
          
          if (startDate) {
            url += `&start_date=${startDate}`;
          }
          
          if (endDate) {
            url += `&end_date=${endDate}`;
          }
        }

        const res = await fetch(url);
        const result = await res.json();

        // Check if the response is from the new API (has filters, summary, statewise_data)
        if (result.filters && result.summary && result.statewise_data) {
          // New API response format
          setData(result.statewise_data || []);
          setSummary(result.summary);
        } else {
          // Old API response format (array of state-wise data)
          setData(result || []);
          setSummary(null);
        }
      } catch (err) {
        console.error("Error fetching MSME data:", err);
        setData([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };

    if (pid) {
      fetchData();
    }
  }, [pid, startDate, endDate]);

  // Totals Calculation for old API format
  const totals = data.reduce(
    (acc, row) => {
      acc.total_entreprenuers += row.total_entreprenuers || 0;
      acc.edp_Count += row.edp_Count || 0;
      acc.yc_msme_Count += row.yc_msme_Count || 0;
      acc.schemes_msme_Count += row.schemes_msme_Count || 0;
      acc.msme_yc_scheme_count += row.msme_yc_scheme_count || 0;
      return acc;
    },
    {
      total_entreprenuers: 0,
      edp_Count: 0,
      yc_msme_Count: 0,
      schemes_msme_Count: 0,
      msme_yc_scheme_count: 0,
    }
  );

  // Use summary data if available (from new API), otherwise use calculated totals
  const displayTotals = summary || totals;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin h-10 w-10 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[90vw] ml-[4vw] mt-[3vh] border-2 border-gray-300 shadow-md rounded-md bg-white">
      {/* Heading */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 p-4">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-pink-800">
            Entrepreneur/MSME Summary
          </h2>
          {/* Show filter information if date filters are applied */}
          {startDate && endDate && (
            <p className="text-xs text-gray-500 mt-1">
              Showing data from {startDate} to {endDate}
            </p>
          )}
          {startDate && !endDate && (
            <p className="text-xs text-gray-500 mt-1">
              Showing data from {startDate} onwards
            </p>
          )}
          {!startDate && endDate && (
            <p className="text-xs text-gray-500 mt-1">
              Showing data up to {endDate}
            </p>
          )}
        </div>

        {/* Date Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDateInput}
              min={startDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>

          <button
            onClick={handleApplyFilter}
            className="bg-pink-600 hover:bg-pink-700 text-white px-5 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Apply Filter
          </button>

          <button
            onClick={handleClearFilter}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Summary Cards (only for new API) */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gray-50 border-b border-gray-200">
          <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
            <p className="text-xs text-gray-500">Total Entrepreneurs</p>
            <p className="text-lg font-bold text-pink-600">
              {summary.total_entreprenuers?.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
            <p className="text-xs text-gray-500">Total EDP Done</p>
            <p className="text-lg font-bold text-blue-600">
              {summary.edp_count?.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
            <p className="text-xs text-gray-500">MSME YC</p>
            <p className="text-lg font-bold text-green-600">
              {summary.yc_msme_count?.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
            <p className="text-xs text-gray-500">MSME Schemes</p>
            <p className="text-lg font-bold text-purple-600">
              {summary.schemes_msme_count?.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
            <p className="text-xs text-gray-500">YC(MSME) + Schemes(MSME)</p>
            <p className="text-lg font-bold text-orange-600">
              {summary.msme_yc_scheme_count?.toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 border-r text-left text-xs font-medium text-gray-600">
                State
              </th>
              <th className="p-3 border-r text-left text-xs font-medium text-gray-600">
                Total Entrepreneurs
              </th>
              <th className="p-3 border-r text-left text-xs font-medium text-gray-600">
                Total EDP Done
              </th>
              <th className="p-3 border-r text-left text-xs font-medium text-gray-600">
                MSME YC
              </th>
              <th className="p-3 border-r text-left text-xs font-medium text-gray-600">
                MSME Schemes
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-600">
                YC(MSME) + Schemes(MSME)
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="p-3 border-r text-sm">
                    {row.state || row.State}
                  </td>
                  <td className="p-3 border-r text-sm">
                    {row.total_entreprenuers?.toLocaleString()}
                  </td>
                  <td className="p-3 border-r text-sm">
                    {row.edp_count || row.edp_Count?.toLocaleString()}
                  </td>
                  <td className="p-3 border-r text-sm">
                    {row.yc_msme_count || row.yc_msme_Count?.toLocaleString()}
                  </td>
                  <td className="p-3 border-r text-sm">
                    {row.schemes_msme_count || row.schemes_msme_Count?.toLocaleString()}
                  </td>
                  <td className="p-3 text-sm">
                    {row.msme_yc_scheme_count?.toLocaleString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="text-center py-6 text-gray-500 font-medium"
                >
                  No Data Found
                </td>
              </tr>
            )}

            {/* Total Row */}
            {data.length > 0 && (
              <tr className="bg-gray-100 font-semibold">
                <td className="p-3 border-r">Total</td>
                <td className="p-3 border-r">
                  {displayTotals.total_entreprenuers?.toLocaleString() || 0}
                </td>
                <td className="p-3 border-r">
                  {displayTotals.edp_count?.toLocaleString() || 
                   displayTotals.edp_Count?.toLocaleString() || 0}
                </td>
                <td className="p-3 border-r">
                  {displayTotals.yc_msme_count?.toLocaleString() || 
                   displayTotals.yc_msme_Count?.toLocaleString() || 0}
                </td>
                <td className="p-3 border-r">
                  {displayTotals.schemes_msme_count?.toLocaleString() || 
                   displayTotals.schemes_msme_Count?.toLocaleString() || 0}
                </td>
                <td className="p-3">
                  {displayTotals.msme_yc_scheme_count?.toLocaleString() || 0}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EnterpriseMsmeTable;